import { useTranslation } from "../context/Internationalization";
import { axiosGraphQL } from "../api";

const useGraphql = () => {
  const { t } = useTranslation();

  // Helper function to safely parse error messages from GraphQL
  const parseErrorMessage = (error) => {
    try {
      return JSON.parse(error.message);
    } catch {
      // If not JSON, return the original error message wrapped in an object
      return { message: error.message };
    }
  };

  // Centralized error handler for GraphQL errors
  const handleGraphqlErrors = (
    errors,
    specificErrorMessage = null,
    transportStatus = null
  ) => {
    const errorList = Array.isArray(errors) ? errors : [errors]; // Always work with an array

    if (!errorList || errorList.length === 0) {
      // No errors available, throw a generic error
      throw new Error(`${t("an_error_occurred")}`);
    }

    if (specificErrorMessage) {
      // If a specific message is passed, prioritize it
      throw new Error(`${specificErrorMessage}`);
    }

    // Map and translate each error message
    const concatErrorMessages = errorList
      .map((error) => {
        const errorObject = parseErrorMessage(error);
        return t(errorObject?.message) || t("an_error_occurred");
      })
      .join(",\n"); // Join multiple errors with a new line

    const failure = new Error(concatErrorMessages);
    // Preserve machine-readable identifiers independently of translations.
    // Do not copy diagnostic messages, payloads, or stack traces into codes.
    failure.codes = [
      ...new Set(
        errorList.flatMap((error) => {
          const parsed = parseErrorMessage(error);
          const candidates = [
            error.extensions?.code,
            parsed?.code,
            parsed?.message,
          ];
          return candidates.filter(
            (code) => typeof code === "string" && /^[A-Z][A-Z0-9_]+$/.test(code)
          );
        })
      ),
    ];
    // Keep the application status from JSON GraphQL errors even on HTTP 200.
    // Mixed statuses cannot safely select one status-specific UI message.
    const statusCodes = [...new Set(errorList.flatMap((error) => [
      parseErrorMessage(error)?.statusCode,
      error.response?.status,
    ]).filter((status) => Number.isInteger(status) && status >= 400 && status <= 599))];
    // A GraphQL body may omit application statuses. Keep the outer HTTP status
    // as a fallback, without overriding explicit or conflicting body statuses.
    if (statusCodes.length === 1) {
      failure.statusCode = statusCodes[0];
    } else if (
      statusCodes.length === 0 && Number.isInteger(transportStatus) &&
      transportStatus >= 400 && transportStatus <= 599
    ) {
      failure.statusCode = transportStatus;
    }
    const cooldown = errorList.find(
      (error) =>
        error.extensions?.code === "REGISTRY_LOOKUP_COOLDOWN_ACTIVE" &&
        Number.isSafeInteger(error.extensions.retryAfterSeconds) &&
        error.extensions.retryAfterSeconds > 0
    );
    if (cooldown) {
      failure.retryAfterSeconds = cooldown.extensions.retryAfterSeconds;
    }
    throw failure;
  };

  // GraphQL query handler
  const handleGraphql = (queryObject, requestOptions) =>
    axiosGraphQL(queryObject, requestOptions)
      .then((response) => {
        // Handle GraphQL-specific errors even if HTTP response is 2xx
        if (response.data.errors) {
          handleGraphqlErrors(response.data.errors);
        }

        return response; // No errors, return the successful response
      })
      .catch((error) => {
        if (Array.isArray(error.codes)) throw error;
        // Handle network, server, or setup errors
        if (error.response) {
          // Server responded with an error status code (e.g., 400, 500)
          if (error.response?.data?.errors?.length) {
            handleGraphqlErrors(error.response.data.errors, null, error.response.status);
          } else {
            handleGraphqlErrors([error]);
          }
        } else if (error.request) {
          // Request was made but no response received (server down, network issues)
          const failure = new Error(t("error_server_not_responding"));
          failure.codes = ["NETWORK_ERROR"];
          throw failure;
        } else {
          // Something went wrong before the request was even sent
          handleGraphqlErrors([error]);
        }
      });

  // Return the GraphQL executor and error handler
  return { axiosGraphQL: handleGraphql, handleGraphqlErrors };
};

export default useGraphql;
